import { LOCAL, NO_RECV_FILTERS, USD, hasRecvFilters, inCurrency, toParam } from '../api/types'
import { gridsOnTab } from '../api/viewScope'
import { restore, snapshot } from '../components/gridLayout'
import { restoreSort, snapshotSort, type SortState } from '../components/gridSort'
import {
  NOTHING_DROPPED,
  SavedViewsMenu,
  VIEW_SCHEMA,
  ViewAdjustedNotice,
  parseView,
  useSavedViews,
  useViewDirty,
  type DropReport,
  type ViewPayload,
} from '../components/SavedViews'
import { CurrencyControl } from '../components/CurrencyControl'
import { useReportCurrency } from '../components/rememberedCurrency'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Api } from '../api'
import type { CurrencyRate, RecvFilters, RecvFilterRow, StoredReaderView } from '../api/types'
import {
  Card,
  CardHeader,
  CollapsibleStats,
  MultiSearchSelect,
  PageTabs,
  ReportHeader,
  NAME_PANEL_WIDTH,
  SearchSelect,
  Segmented,
  currencyShort,
  inputClass,
} from '../components/ui'
import { ExpandableCard } from '../components/ExpandableCard'
import { ReceivablesMatrix } from '../components/ReceivablesMatrix'
import { TransactionsTable } from '../components/TransactionsTable'
import { DecompositionTree } from '../components/DecompositionTree'
import { DsoTable } from '../components/DsoTable'
import { KpiStrip } from '../components/KpiStrip'
import { WeekComparison } from '../components/WeekComparison'
import { TrendChart } from '../components/TrendChart'
import { SalespersonReview } from '../components/SalespersonReview'
import { ExportButtons } from '../components/ExportButtons'
import type { ExportDoc } from '../export/model'
import { DevModeProvider } from '../components/MeasurePanel'
import { Explain } from '../components/DevModeContext'

/**
 * RECEIVABLES — the Fabric report, rebuilt, as a set of tabs.
 *
 * The Power BI report is six pages inside one report, and this mirrors that:
 * ONE entry in the sidebar, pages as tabs across the top. The alternative —
 * every page its own sidebar item — loses the grouping into "one report" and
 * makes the sidebar grow with every report added later.
 *
 * One of the .pbix's six pages is deliberately absent:
 *   * "User Permission Review" — a table of manager email addresses. This
 *     platform has its own permission screen with a real access model behind
 *     it; rebuilding a list of addresses would be a second, worse answer to the
 *     same question.
 *
 * "Week comparison" is here but it is NOT a port. In the .pbix it is a slicer on
 * a table that does not exist in the database, with no visual for it to filter —
 * it does nothing. The question it was reaching for is a real one, so it is
 * built here properly: four metrics, four dimensions, two weeks, and the change
 * between them, all of it read through the same authorization every other tab
 * goes through.
 */

type TabId = 'dashboard' | 'transactions' | 'dso' | 'tree' | 'weeks' | 'trends' | 'review'

/* ===========================================================================
   THE STANDING FILTER BAR

   WHAT THE POWER BI REPORT ACTUALLY HAD, read back out of
   Receivables Dashboard.pbix on 24 Sep 2026. One slicer in six pages — a
   "Week Label" dropdown bound to a table that does not exist in the database
   and filters no visual. Everything else lived in the FILTERS PANE: company,
   business unit, business team, salesperson, customer, document number, Days
   Old, Days Overdue and a threshold on every aging bucket. Exactly one of them
   carried a saved condition: Days Old > 120, on a DUPLICATE decomposition tree
   titled "Over 120 Days" that exists only because a filter pane cannot be put
   on the canvas. That duplicate visual is the clearest statement of what was
   wanted, and one Aging period menu replaces it.

   WHICH TABS OBEY WHICH CONTROL IS A PROPERTY OF THE DATA, not a preference,
   and the bar draws only what the active tab can honour:

     Aging Dashboard, By Transaction   all twelve, plus the key figures above
                                       them — the strip rides the same root
                                       call the matrix makes, so the headline
                                       and the grid cannot describe two
                                       different populations
     Salesperson DSO                   four. D365_Salesperson_DSO has nine
                                       columns and carries no customer,
                                       document, aging period, payment terms
                                       or region
     Week Comparison                   five. The intersection of four different
                                       sources — and, not by coincidence, the
                                       exact grain rpt.WeeklyMetricFact_MBI stores,
                                       so a filtered week still answers from
                                       the snapshot instead of rescanning the
                                       ledger

   A CONTROL THAT CHANGES NOTHING IS WORSE THAN AN ABSENT ONE: the reader sets
   it, the figure holds still, and they conclude the report is wrong rather
   than that the control is. So anything a tab cannot honour is not drawn — and
   when a filter set elsewhere is being ignored here, the bar says so in a line
   rather than leaving the reader to assume it applied.
   =========================================================================== */

/** What each tab can actually narrow by. */
const FULL_FILTER_TABS: TabId[] = ['dashboard', 'transactions']
const FILTERED_TABS: TabId[] = ['dashboard', 'transactions', 'dso', 'weeks']

/** The label each filter wears, for the "not honoured here" sentence and the extracts. */
const FILTER_LABEL: Record<keyof RecvFilters, string> = {
  company: 'Company',
  bu: 'Business unit',
  bt: 'Business team',
  sp: 'Salesperson',
  cust: 'Customer',
  aging: 'Aging period',
  trxType: 'Transaction type',
  terms: 'Payment terms',
  region: 'Customer region',
  doc: 'Document',
  overdueOnly: 'Overdue only',
  minDaysOverdue: 'Days overdue at least',
}

/** One picker: an explainable eyebrow, a SearchSelect, and an "All" that clears it. */
function Pick({
  label,
  measureKey,
  value,
  onChange,
  options,
  panelWidth,
}: {
  label: string
  measureKey: string
  value: string | undefined
  onChange: (v: string | undefined) => void
  options: RecvFilterRow[]
  /** Set it for a menu of NAMES; see NAME_PANEL_WIDTH for the measurements. */
  panelWidth?: number
}) {
  // A MENU WITH NOTHING IN IT IS NOT A MENU. The server returns only the
  // members this caller may see, so an empty list means this reader's book has
  // no such dimension — drawing "All" over nothing invites a click that can
  // only disappoint.
  if (options.length === 0) return null
  return (
    <div className="flex min-w-[150px] max-w-[230px] flex-1 flex-col gap-1">
      <Explain surface="filters" measureKey={measureKey} className="eyebrow">
        {label}
      </Explain>
      <SearchSelect
        label={label}
        panelWidth={panelWidth}
        value={value ?? ''}
        onChange={(v) => onChange(v || undefined)}
        options={[
          { value: '', label: 'All' },
          ...options.map((o) => ({ value: o.code ?? '', label: o.label || '(blank)' })),
        ]}
      />
    </div>
  )
}

/**
 * A typed filter — customer and document, which are substrings rather than
 * menus.
 *
 * DEBOUNCED, and Enter commits at once. Copied in behaviour from Sales'
 * customer box on purpose: 5,373 distinct customer names here against 9,742
 * there, and a reader holding both reports should not meet two behaviours for
 * one keystroke. Every character committed immediately would be one audited
 * read per keystroke against a 171,354-row view.
 */
function TypedFilter({
  label,
  measureKey,
  placeholder,
  value,
  onChange,
}: {
  label: string
  measureKey: string
  placeholder: string
  value: string | undefined
  onChange: (v: string | undefined) => void
}) {
  const [text, setText] = useState(value ?? '')
  const committed = useRef(value ?? '')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Follows the value when something else changes it — "Clear filters", or a
  // reset when the reader is switched to somebody else's eyes.
  useEffect(() => {
    const next = value ?? ''
    if (next === committed.current) return
    committed.current = next
    setText(next)
  }, [value])

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
  }, [])

  const commit = (next: string) => {
    const trimmed = next.trim()
    if (trimmed === committed.current) return
    committed.current = trimmed
    onChange(trimmed || undefined)
  }

  return (
    <div className="flex min-w-[150px] max-w-[230px] flex-1 flex-col gap-1">
      <Explain surface="filters" measureKey={measureKey} className="eyebrow">
        {label}
      </Explain>
      <input
        type="search"
        value={text}
        onChange={(e) => {
          setText(e.target.value)
          if (timer.current) clearTimeout(timer.current)
          timer.current = setTimeout(() => commit(e.target.value), 400)
        }}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return
          if (timer.current) clearTimeout(timer.current)
          commit(text)
        }}
        placeholder={placeholder}
        aria-label={label}
        className={inputClass}
      />
    </div>
  )
}

/**
 * "Days overdue at least" — the .pbix's one real saved condition, generalised.
 *
 * MEASURED AGAINST Days_Overdue, NOT Days Old, which is what the .pbix used.
 * Days Old is the document's age; on a 90-day payment term a document 120 days
 * old is 30 days late, and "120 days late" is the question people arrive with.
 * The difference is not cosmetic: measured on the clone 24 Sep 2026, Days Old
 * over 120 selects 10,332 of the 19,405 open rows and Days Overdue over 120
 * selects 7,418.
 */
function DaysOverdueFilter({
  value,
  onChange,
}: {
  value: number | undefined
  onChange: (v: number | undefined) => void
}) {
  const [text, setText] = useState(value === undefined ? '' : String(value))
  const committed = useRef(value)

  useEffect(() => {
    if (value === committed.current) return
    committed.current = value
    setText(value === undefined ? '' : String(value))
  }, [value])

  const commit = () => {
    const trimmed = text.trim()
    // Anything that is not a whole number is no filter at all rather than an
    // error: this is a text box and a half-typed minus sign is not a mistake
    // worth a red message.
    const n = trimmed === '' ? undefined : Number.parseInt(trimmed, 10)
    const next = n === undefined || Number.isNaN(n) || n < 0 ? undefined : n
    if (next === committed.current) return
    committed.current = next
    setText(next === undefined ? '' : String(next))
    onChange(next)
  }

  return (
    <div className="flex w-[150px] flex-col gap-1">
      <Explain surface="filters" measureKey="minDaysOverdue" className="eyebrow">
        Days overdue at least
      </Explain>
      <input
        type="number"
        min={0}
        inputMode="numeric"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit()
        }}
        placeholder="Any"
        aria-label="Days overdue at least"
        className={inputClass}
      />
    </div>
  )
}

function RecvFilterBar({
  tab,
  value,
  onChange,
  options,
}: {
  tab: TabId
  value: RecvFilters
  onChange: (f: RecvFilters) => void
  /* LOADED BY THE PAGE, not here. It was this component's own state until
     24 Sep 2026, which was fine while the bar was the only thing that needed
     it -- but applying a saved view has to DROP a filter naming a company the
     reader can no longer see, and that decision needs the same list. Two
     fetches of one thing is two answers waiting to disagree. */
  options: RecvFilterRow[] | null
}) {
  const [more, setMore] = useState(false)

  /* THE VIEW-AS RESET IS NOT HERE. It lives on the page, which owns `filters`
     and stays mounted across tab changes -- see the note beside that effect. In
     here it fired on every mount, and this bar is drawn on four tabs of seven,
     so returning from Tree View cleared filters the screen said were kept. */

  const of = (kind: RecvFilterRow['kind']) => (options ?? []).filter((o) => o.kind === kind)
  const set = <K extends keyof RecvFilters>(k: K) => (v: RecvFilters[K]) =>
    onChange({ ...value, [k]: v })

  const full = FULL_FILTER_TABS.includes(tab)
  const showCustomer = tab !== 'dso'

  /* WHAT IS SET BUT NOT HONOURED HERE. A reader who narrows to an aging period
     on the dashboard and then opens Salesperson DSO would otherwise read those
     figures as filtered. The filters are KEPT rather than cleared — coming back
     to the dashboard should restore the question they were asking — so the only
     honest thing left is to say which ones this tab is ignoring. */
  const ignored = (Object.keys(FILTER_LABEL) as (keyof RecvFilters)[]).filter((k) => {
    const isSet = k === 'company' ? Boolean(value.company?.length) : Boolean(value[k])
    if (!isSet) return false
    if (k === 'cust') return !showCustomer
    if (['company', 'bu', 'bt', 'sp'].includes(k)) return false
    return !full
  })

  // What is hidden behind "More filters", so a reader can never lose one there.
  const moreCount = full
    ? [value.trxType, value.terms, value.region, value.doc, value.overdueOnly || undefined, value.minDaysOverdue]
        .filter((v) => v !== undefined && v !== '').length
    : 0

  return (
    <div className="no-print shrink-0 space-y-2">
      <div className="flex flex-wrap items-end gap-3">
        {/* COMPANY IS MULTI-SELECT, as it is on Sales: most readers hold several
            and the question is usually "these three", not "this one" or "all
            forty". It filters by CODE and displays by name — measured 24 Sep
            2026, all 39 codes in the open book carry exactly one name. */}
        {of('company').length > 0 && (
          <div className="flex min-w-[170px] max-w-[250px] flex-1 flex-col gap-1">
            <Explain surface="filters" measureKey="company" className="eyebrow">
              Company
            </Explain>
            <MultiSearchSelect
              label="Company"
              noun="companies"
              /* The panel, not the trigger. Company names render up to 422px
                 and their p90 is 345, so a panel sized to its own 250px
                 trigger cut ORDINARY entries and left two "REDA..." companies
                 indistinguishable. */
              panelWidth={NAME_PANEL_WIDTH}
              value={value.company ?? []}
              onChange={(v) => onChange({ ...value, company: v.length ? v : undefined })}
              /* Deduped by code before it reaches the picker: MultiSearchSelect
                 keys its options by `value`, and two companies sharing a code
                 would render duplicate React keys. */
              options={[
                ...new Map(
                  of('company').map((o) => [
                    o.code ?? '',
                    {
                      value: o.code ?? '',
                      label: o.label || '(blank)',
                      hint: o.code !== o.label ? (o.code ?? undefined) : undefined,
                    },
                  ]),
                ).values(),
              ]}
            />
          </div>
        )}
        <Pick label="Business unit" measureKey="bu" value={value.bu} onChange={set('bu')} options={of('businessUnit')} />
        <Pick label="Business team" measureKey="bt" value={value.bt} onChange={set('bt')} options={of('businessTeam')} />
        {/* ABSENT, NOT EMPTY, for a reader without SLS_Mod: the server sends no
            salesperson rows at all, so Pick returns null and there is no
            control. Offering one and refusing it would still confirm the
            dimension exists — and a menu of 403 names IS the roster. */}
        <Pick label="Salesperson" measureKey="sp" value={value.sp} onChange={set('sp')} options={of('salesperson')} panelWidth={NAME_PANEL_WIDTH} />
        {showCustomer && (
          <TypedFilter
            label="Customer contains"
            measureKey="cust"
            /* SHORT because the row is full: six controls and two buttons at
               1536px leave each about 150px, and "Any part of the name…" was
               being cut mid-word. The eyebrow above already says "contains",
               so the placeholder only has to say how much of it. */
            placeholder="Any part…"
            value={value.cust}
            onChange={set('cust')}
          />
        )}
        {full && (
          <Pick label="Aging period" measureKey="aging" value={value.aging} onChange={set('aging')} options={of('agingPeriod')} />
        )}
        {full && (
          <button
            type="button"
            onClick={() => setMore((m) => !m)}
            aria-expanded={more}
            className="rounded-md border border-(--color-line-strong) px-3 py-2 text-(length:--text-small) font-semibold text-(--color-ink-2) hover:bg-(--color-surface-2) hover:text-(--color-ink)"
          >
            {more ? 'Fewer filters' : 'More filters'}
            {moreCount > 0 && !more ? ` (${moreCount})` : ''}
          </button>
        )}
        {hasRecvFilters(value) && (
          <button
            type="button"
            onClick={() => onChange(NO_RECV_FILTERS)}
            className="rounded-md border border-(--color-line-strong) px-3 py-2 text-(length:--text-small) font-semibold text-(--color-ink-2) hover:bg-(--color-surface-2) hover:text-(--color-ink)"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* THE SECOND ROW IS BEHIND A CLICK because height is the scarce axis on
          this page — twelve controls open would take three rows at 1536x864 and
          leave the grid a handful of lines. The six here answer narrower
          questions than the six above, and the button carries a count so a
          reader can never lose a filter behind it. */}
      {full && more && (
        <div className="flex flex-wrap items-end gap-3">
          {/* Payment rows carry NEGATIVE money (measured -1.44B against Sales'
              +44.4B on 24 Sep 2026) and net silently into every total on this
              report. This is the only way to separate them. */}
          <Pick label="Transaction type" measureKey="trxType" value={value.trxType} onChange={set('trxType')} options={of('trxType')} />
          <Pick label="Payment terms" measureKey="terms" value={value.terms} onChange={set('terms')} options={of('paymentTerms')} panelWidth={NAME_PANEL_WIDTH} />
          <Pick label="Customer region" measureKey="region" value={value.region} onChange={set('region')} options={of('region')} />
          <TypedFilter
            label="Document contains"
            measureKey="doc"
            placeholder="Part of the number…"
            value={value.doc}
            onChange={set('doc')}
          />
          <div className="flex flex-col gap-1">
            <Explain surface="filters" measureKey="overdueOnly" className="eyebrow">
              Due state
            </Explain>
            {/* NOT the same question as the aging period: 1,453 of the 19,405
                open rows are not yet due at all (720M, measured 24 Sep 2026),
                and they sit in the book without being a collections problem. */}
            <Segmented
              label="Due state"
              value={value.overdueOnly ? 'overdue' : 'all'}
              onChange={(v) => onChange({ ...value, overdueOnly: v === 'overdue' ? true : undefined })}
              options={[
                { value: 'all', label: 'All open', title: 'Every open document, due or not' },
                { value: 'overdue', label: 'Overdue only', title: 'Only documents past their due date' },
              ]}
            />
          </div>
          <DaysOverdueFilter value={value.minDaysOverdue} onChange={set('minDaysOverdue')} />
        </div>
      )}

      {ignored.length > 0 && (
        <p className="text-(length:--text-small) text-(--color-ink-3)">
          This screen cannot narrow by {ignored.map((k) => FILTER_LABEL[k].toLowerCase()).join(', ')} — the figures
          behind it do not carry {ignored.length === 1 ? 'that column' : 'those columns'}. Everything else on the bar
          applies. {ignored.length === 1 ? 'That filter is' : 'Those filters are'} kept, and {ignored.length === 1 ? 'takes' : 'take'} effect again on the Aging Dashboard and By Transaction.
        </p>
      )}
    </div>
  )
}

interface Tab {
  id: TabId
  label: string
  /** Set while a page is still being built, so the tab is honest rather than absent. */
  pending?: string
}

const TABS: Tab[] = [
  { id: 'dashboard', label: 'Aging Dashboard' },
  { id: 'transactions', label: 'By Transaction' },
  { id: 'dso', label: 'Salesperson DSO' },
  { id: 'tree', label: 'Tree View' },
  { id: 'weeks', label: 'Week Comparison' },
  { id: 'trends', label: 'Trends' },
  { id: 'review', label: 'Salesperson Review' },
]

export function ReceivablesAgingPage({
  api,
  viewAs = null,
  roles = [],
  actor = null,
}: {
  api: Api
  viewAs?: string | null
  /** For developer mode. Only an affordance — the API refuses the panel to
      anyone who is not a System Admin whatever this says. */
  roles?: string[]
  /** Signed-in identity, printed on the review so paper is attributable. */
  actor?: string | null
}) {
  const [tab, setTab] = useState<TabId>('dashboard')
  /* Currency is REPORT-level, not per tab: switching to By Transaction with the
     matrix still in USD and the detail in local would put two currencies on one
     screen, and nothing on either would say so.

     USD BY DEFAULT since 23 Sep 2026 — see the note in SalesPage for why. Two
     things on this report deliberately stay local whatever this says, and both
     already explain themselves on screen: Salesperson DSO, whose two amount
     columns are a ratio in one currency and so read the same either way, and
     the collections trend, which has no dollar figure in the source at all. */
  /* Remembered since 4 Oct 2026, across every report: see
     components/rememberedCurrency.ts and the note in SalesPage. */
  const [currency, chooseCurrency, setCurrency] = useReportCurrency(api, viewAs)
  /* THE STANDING FILTERS, held at REPORT level and not per tab, for the same
     reason currency is: a reader who narrows the Aging Dashboard to one team
     and opens By Transaction is still asking about that team. Kept across every
     tab including the three that ignore them, so coming back restores the
     question rather than making it be asked again — the bar says plainly when a
     tab is ignoring one. */
  const [filters, setFilters] = useState<RecvFilters>(NO_RECV_FILTERS)

  /* A DIFFERENT PAIR OF EYES IS A DIFFERENT SCOPE, so the filters are dropped
     when the reader switches who they are looking as: one carried across would
     silently show nothing and read as an empty book.

     IT LIVES HERE, ON THE PAGE, AND NOT IN THE BAR. It was in RecvFilterBar
     until 24 Sep 2026, which made it a bug rather than a rule: the bar is drawn
     only on the four tabs that can honour a filter, so opening Tree View
     unmounted it and coming back MOUNTED it again -- and an effect fires on
     mount. Setting a filter, glancing at the tree and returning silently
     cleared the bar, while the sentence under it went on promising the filter
     was kept. The page owns `filters` and does not unmount when the tab
     changes, so here the effect fires when the identity changes and at no other
     time. On mount it is a no-op: NO_RECV_FILTERS is a module constant, so
     useState sees the same reference and bails out.

     Sales never had this because SalesFilterBar is drawn on every tab. The fix
     makes the two reports agree rather than giving Receivables a behaviour of
     its own. */
  useEffect(() => {
    setFilters(NO_RECV_FILTERS)
  }, [viewAs])

  /* THE FILTER MENUS, loaded once for the whole report. They fill the bar, and
     they are also what lets a saved view drop a filter naming a company this
     reader can no longer see -- so they belong to the page, which owns both. */
  const [filterOptions, setFilterOptions] = useState<RecvFilterRow[] | null>(null)
  useEffect(() => {
    let cancelled = false
    setFilterOptions(null)
    api
      .receivablesFilterOptions()
      .then((r) => !cancelled && setFilterOptions(r))
      /* A FAILED MENU IS NOT A REFUSED REPORT. The grids have their own gates
         and say their own refusal; a bar that cannot fill draws nothing rather
         than stacking a second panel over the first. */
      .catch(() => !cancelled && setFilterOptions([]))
    return () => {
      cancelled = true
    }
  }, [api, viewAs])

  /* THE LIVE RATES, so a saved view's currency is restated at today's rate and
     not at the one in force when it was saved. One small call per report open;
     CurrencyControl fetches the same list for its picker. */
  const [rates, setRates] = useState<CurrencyRate[] | null>(null)
  useEffect(() => {
    let cancelled = false
    api.currencyRates().then((r) => !cancelled && setRates(r)).catch(() => !cancelled && setRates([]))
    return () => { cancelled = true }
  }, [api])

  /* ---------------------------------------------------------------------
     SAVED VIEWS.

     capture() and apply() live HERE, beside the state they read, rather than
     in a shared adapter file -- which is where they would rot the first time
     this page gains a picker and nobody remembered a second file existed.
     Everything the shared component does treats the payload as a string.
     --------------------------------------------------------------------- */
  const { views, save, remove, rename, setDefault } = useSavedViews(api, 'receivables', viewAs)
  const [activeView, setActiveView] = useState<number | null>(null)
  const [adjusted, setAdjusted] = useState<DropReport>(NOTHING_DROPPED)
  /** Which tab's default has already been applied, so it happens once. */
  const appliedDefault = useRef<Set<string>>(new Set())

  const tabViews = useMemo(() => views.filter((v) => v.pageSlug === tab), [views, tab])

  const capture = useCallback((): ViewPayload => {
    const grids: Record<string, { order?: string[]; hidden?: string[]; sort?: SortState }> = {}
    for (const gridId of gridsOnTab('receivables', tab)) {
      const snap = snapshot(gridId)
      const sorted = snapshotSort(gridId)
      /* ORDER, HIDDEN AND THE SORT -- never widths or pinning. Those are
         physical and screen-dependent, and a view that also resized columns
         would fight the reader's own drag every time they switched views. The
         silent layer already keeps them, per person per grid. */
      if (!snap && !sorted?.key) continue
      grids[gridId] = {
        ...(snap ? { order: snap.order, hidden: snap.hidden } : {}),
        ...(sorted?.key ? { sort: sorted } : {}),
      }
    }
    return { v: VIEW_SCHEMA, currency: toParam(currency) ?? 'local', filters, grids }
  }, [tab, currency, filters])

  const apply = useCallback(
    (payload: ViewPayload): DropReport => {
      const dropped: string[] = []

      /* FILTERS: a value the reader can no longer be offered is left out, not
         applied. It could never disclose anything -- a filter can only narrow,
         and the procedure re-checks scope regardless -- but a control reading
         "AL REDA" over a book that does not contain it is a screen lying about
         what it is showing. */
      const stored = (payload.filters ?? {}) as RecvFilters
      const next: RecvFilters = {}
      const offered = (kind: RecvFilterRow['kind'], code: string) =>
        !filterOptions || filterOptions.some((o) => o.kind === kind && o.code === code)

      const keep = <K extends keyof RecvFilters>(k: K, kind: RecvFilterRow['kind'] | null) => {
        const v = stored[k]
        if (v === undefined || v === '' || v === false) return
        if (kind && typeof v === 'string' && !offered(kind, v)) {
          dropped.push(FILTER_LABEL[k].toLowerCase())
          return
        }
        next[k] = v
      }

      if (stored.company?.length) {
        const live = stored.company.filter((c) => offered('company', c))
        if (live.length) next.company = live
        if (live.length !== stored.company.length) dropped.push('some companies')
      }
      keep('bu', 'businessUnit')
      keep('bt', 'businessTeam')
      keep('sp', 'salesperson')
      keep('aging', 'agingPeriod')
      keep('trxType', 'trxType')
      keep('terms', 'paymentTerms')
      keep('region', 'region')
      /* The two typed boxes match a SUBSTRING and have no list to check
         against, so there is nothing that could make them stale. */
      keep('cust', null)
      keep('doc', null)
      keep('overdueOnly', null)
      keep('minDaysOverdue', null)
      setFilters(next)

      /* CURRENCY: the CODE is stored, never the rate.
         The client's `rate` is display-only -- the server converts by code, and
         the number is used solely for the "restated at 3.6725 per US dollar"
         caption. Storing it would mean a view saved today printing last
         quarter's rate over figures converted at this one: the label and the
         amount disagreeing, which is the defect the served-currency gate was
         built to refuse. So the rate is looked up live, and a code that is no
         longer offerable is dropped like any other stale value. */
      const code = typeof payload.currency === 'string' ? payload.currency : 'local'
      if (code === 'local') setCurrency(LOCAL)
      else if (code === 'usd') setCurrency(USD)
      else if (rates) {
        const live = rates.find((r) => r.code.toLowerCase() === code.toLowerCase())
        if (live) setCurrency(inCurrency(live.code, live.ratePerUsd))
        else dropped.push(`the ${code.toUpperCase()} currency`)
      }

      /* GRIDS: restore() reconciles against the live columns, so a saved order
         naming a column this reader cannot see keeps the rest rather than
         resetting the grid. It reports nothing back, which is why a dropped
         column is not listed above -- the grid shows what it has. */
      const grids = (payload.grids ?? {}) as Record<string, { order?: string[]; hidden?: string[]; sort?: { key?: unknown; desc?: unknown } }>
      for (const [gridId, g] of Object.entries(grids)) {
        const current = snapshot(gridId)
        if (!current || !Array.isArray(g.order)) continue
        restore(gridId, {
          ...current,
          order: g.order.filter((k) => current.order.includes(k)),
          hidden: (g.hidden ?? []).filter((k) => current.order.includes(k)),
        })
      }

      /* THE SORT, for every grid on this tab and not only the ones the view
         mentions: a view is the whole arrangement, so one saved with no sort
         puts the grid back to its natural order rather than leaving the last
         question the reader asked sitting over somebody else's answer. Each
         grid checks the key against its own columns -- see useSortMirror. */
      for (const gridId of gridsOnTab('receivables', tab)) {
        const s = grids[gridId]?.sort
        restoreSort(
          gridId,
          s && typeof s.key === 'string' && s.key ? { key: s.key, desc: s.desc !== false } : { key: null, desc: true },
        )
      }

      return { dropped }
    },
    [filterOptions, rates, tab],
  )

  /* Has the screen moved since the view was applied? The name sits beside
     the page title now, so it is a caption and has to stay true. */
  const viewDirty = useViewDirty(views, activeView, capture)

  const applyStored = useCallback(
    (v: StoredReaderView) => {
      const payload = parseView(v.view, v.schemaVersion)
      if (!payload) return
      setAdjusted(apply(payload))
      setActiveView(v.viewId)
    },
    [apply],
  )

  /* THE DEFAULT, ONCE PER TAB, AND ONLY AFTER THE MENUS HAVE LANDED. Applying
     before the option lists arrive would mean deciding what to drop without
     knowing what is offered -- so every filter would survive and then need
     retracting, which is a filter appearing and vanishing in front of the
     reader. Waiting is the simpler and the honest one. */
  useEffect(() => {
    if (!filterOptions || !rates) return
    if (appliedDefault.current.has(tab)) return
    const def = views.find((v) => v.pageSlug === tab && v.isDefault)
    appliedDefault.current.add(tab)
    if (def) applyStored(def)
  }, [filterOptions, rates, views, tab, applyStored])

  // A different pair of eyes means different views and a different book.
  useEffect(() => {
    appliedDefault.current = new Set()
    setActiveView(null)
    setAdjusted(NOTHING_DROPPED)
  }, [viewAs])

  const active = TABS.find((t) => t.id === tab) ?? TABS[0]

  // The Aging Dashboard and By Transaction extract buttons live in the card
  // HEADER, not on a row of their own — one less strip of chrome above a grid
  // that is already tight for height. buildDoc closes over the child's loaded
  // rows and sort/page state, so it stays in the child; the child writes it
  // into these refs and the header reads .current only at click time.
  const matrixExport = useRef<(() => ExportDoc | null) | null>(null)
  const txnExport = useRef<(() => ExportDoc | null) | null>(null)

  /* A COLUMN THAT FILLS THE VIEWPORT.
     The title, the tabs and the KPI strip are chrome: they stay put while the
     grid moves under them. Before this, scrolling a 15-unit matrix carried the
     tab strip off the screen, so changing page meant scrolling back up first.

     Each chrome block is shrink-0; the tab body is flex-1 min-h-0 and owns the
     only scrollbar on the page. */
  return (
    /* Developer mode, once, for the whole report -- see MeasurePanel for why it
       is a context rather than a prop. `request` carries only what is true of
       every tab; each surface adds its own through Explain's `extra`. */
    <DevModeProvider
      api={api}
      roles={roles}
      report="receivables"
      request={{ Currency: currencyShort(currency), Tab: active.label }}
    >
    <div className="stack-tight flex h-full min-h-0 flex-col gap-5">
      {/* "Open items only" is a STATEMENT here, not a control -- the procedure
          takes an include-closed parameter and no screen offers it. So the
          sentence itself is the handle: clicking it explains that open means a
          1900-01-01 sentinel rather than a null close date, which is the first
          thing anybody writing their own query against this view gets wrong.
          It is passed as `what` for that reason — the shared header takes a
          node, not a string, precisely so a report can make its own opening
          words mean something. */}
      <ReportHeader
        report="Receivables"
        screen={active.label}
        what={
          <Explain surface="filters" measureKey="includeClosed">
            Open items only.
          </Explain>
        }
        viewAs={viewAs}
        view={
          <SavedViewsMenu
            views={tabViews}
            activeId={activeView}
            acting={viewAs}
            dirty={viewDirty}
            onApply={applyStored}
            onRename={rename}
            onSave={async (name, makeDefault) => {
              setActiveView(await save(tab, name, capture(), makeDefault))
            }}
            onDelete={(id) => {
              void remove(id)
              if (id === activeView) setActiveView(null)
            }}
            onSetDefault={(id, on) => void setDefault(id, on)}
          />
        }
        right={
          /* Not offered on the DSO tab: days outstanding is a ratio of two
             amounts in the same currency, so it is the same number either way,
             and a control that changes nothing invites the reader to believe it
             did. Its two amount columns stay local, which the card says. */
          /* The views menu sits BESIDE the currency control, which is one of
             the things a view captures -- so the control that manages views is
             next to a control it remembers. It is drawn on every tab,
             including the three with no filter bar: a view of Tree View or
             Trends carries its pickers and currency, which is still a screen
             worth returning to. */
          tab !== 'dso' ? (
            <CurrencyControl api={api} surface="filters" value={currency} onChange={chooseCurrency} />
          ) : null
        }
      />

      {/* Tabs as an underlined rail rather than pills: it reads as pages within
          one document, which is what they are. See PageTabs in ui.tsx. */}
      <PageTabs tabs={TABS} value={tab} onChange={setTab} label="Receivables pages" />

      {/* THE FILTER BAR, on the four tabs whose sources can honour it. Drawn
          between the tabs and the figures, where Sales and Material Planning
          put theirs, so a reader who holds more than one report meets it in
          the same place. It is absent on Tree View, Trends and Salesperson
          Review rather than inert: those three answer from the weekly
          snapshot or their own pickers. */}
      <ViewAdjustedNotice report={adjusted} onDismiss={() => setAdjusted(NOTHING_DROPPED)} />

      {FILTERED_TABS.includes(tab) && (
        <RecvFilterBar tab={tab} value={filters} onChange={setFilters} options={filterOptions} />
      )}

      {(tab === 'dashboard' || tab === 'transactions') && (
        <div className="shrink-0">
          {/* Collapsible so the matrix / transactions grid below can take the
              whole viewport when the reader wants the rows, not the headline.
              Keyed to Receivables so the choice is its own, separate from Sales. */}
          <CollapsibleStats storageKey="stats:receivables">
            <KpiStrip api={api} viewAs={viewAs} currency={currency} filters={filters} />
          </CollapsibleStats>
        </div>
      )}

      {tab === 'dashboard' && (
        <ExpandableCard
          breadcrumb={['Receivables', 'Aging Dashboard']}
          title="By business unit, team, salesperson, customer and document"
          subtitle={`Expand a row to drill down. Click a column heading to sort. Amounts in ${currencyShort(currency)}.`}
          right={<ExportButtons build={() => matrixExport.current?.() ?? null} />}
        >
          <ReceivablesMatrix api={api} viewAs={viewAs} currency={currency} filters={filters} exportRef={matrixExport} />
        </ExpandableCard>
      )}

      {tab === 'transactions' && (
        <ExpandableCard
          breadcrumb={['Receivables', 'By Transaction']}
          title="Open documents"
          subtitle={`One row per document, in ${currencyShort(currency)}. Sorting and paging happen on the server, so a sort orders every document you can see — not just the page in front of you.`}
          right={<ExportButtons build={() => txnExport.current?.() ?? null} />}
        >
          <TransactionsTable api={api} viewAs={viewAs} currency={currency} filters={filters} exportRef={txnExport} />
        </ExpandableCard>
      )}

      {tab === 'tree' && (
        <ExpandableCard
          breadcrumb={['Receivables', 'Tree View']}
          title="Decomposition by amount remaining"
          subtitle={`Choose a field to break down by, then click a bar to go deeper, in ${currencyShort(currency)}. The fields offered are the ones you are permitted to see.`}
        >
          <DecompositionTree api={api} viewAs={viewAs} currency={currency} />
        </ExpandableCard>
      )}

      {tab === 'weeks' && (
        <ExpandableCard
          breadcrumb={['Receivables', 'Week Comparison']}
          title="This week against another"
          subtitle={`Pick what to measure, how to group it, and which two weeks. Rows are sorted by the size of the change, largest first — a team that moved is more interesting than a team that is big. Weeks run Sunday to Saturday.`}
        >
          <WeekComparison api={api} viewAs={viewAs} currency={currency} filters={filters} />
        </ExpandableCard>
      )}

      {/* Trends carries a chart AND the table under it. The table is the longer
          half and the one that benefits from the space. */}
      {tab === 'trends' && (
        <ExpandableCard
          breadcrumb={['Receivables', 'Trends']}
          title="Twelve weeks"
          subtitle="One measure plotted week by week, as a whole book or split by unit, team, company or salesperson. The six largest series are drawn; the rest are folded into one."
        >
          <TrendChart api={api} viewAs={viewAs} currency={currency} />
        </ExpandableCard>
      )}

      {tab === 'review' && (
        /* flush + a no-print header: on paper the card chrome and its screen
           title would frame and duplicate the report's own header block. */
        <Card flush className="scroll-slim min-h-0 flex-1 overflow-y-auto print:overflow-visible print:border-0 print:shadow-none">
          <div className="no-print">
            <CardHeader
              title="Printable performance review"
              subtitle="Four measures for one salesperson across two weeks, each broken down by customer. Built for paper: the Print button produces an A4 sheet carrying whose figures they are, which weeks, which currency and who generated it."
            />
          </div>
          <SalespersonReview api={api} viewAs={viewAs} currency={currency} actor={actor} />
        </Card>
      )}

      {tab === 'dso' && (
        <ExpandableCard
          breadcrumb={['Receivables', 'Salesperson DSO']}
          title="Days sales outstanding by salesperson"
          subtitle="Company → business unit → team → salesperson. DSO is receivables ÷ 365-day sales × 365, computed on the rolled-up sums at every level. Amounts are in local currency; the ratio is the same in any."
        >
          <DsoTable api={api} viewAs={viewAs} filters={filters} />
        </ExpandableCard>
      )}
    </div>
    </DevModeProvider>
  )
}
